import { useEffect, useState, useCallback } from "react";
import api, { apiErr } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { DAYS, subjectsForTrack, examOfTrack } from "@/lib/constants";
import { Wand2, Loader2, Printer, ChevronLeft, ChevronRight, Plus, Trash2, BookOpen, RotateCcw, CalendarDays, Table } from "lucide-react";

const SUBJ_STYLE = {
  "Matematik": { card: "bg-blue-50 border-blue-200", title: "text-blue-700", pill: "bg-blue-600" },
  "Geometri": { card: "bg-indigo-50 border-indigo-200", title: "text-indigo-700", pill: "bg-indigo-600" },
  "Türkçe": { card: "bg-amber-50 border-amber-200", title: "text-amber-700", pill: "bg-amber-500" },
  "Edebiyat": { card: "bg-pink-50 border-pink-200", title: "text-pink-700", pill: "bg-pink-600" },
  "Fizik": { card: "bg-emerald-50 border-emerald-200", title: "text-emerald-700", pill: "bg-emerald-600" },
  "Kimya": { card: "bg-teal-50 border-teal-200", title: "text-teal-700", pill: "bg-teal-600" },
  "Biyoloji": { card: "bg-green-50 border-green-200", title: "text-green-700", pill: "bg-green-600" },
  "Tarih": { card: "bg-rose-50 border-rose-200", title: "text-rose-700", pill: "bg-rose-600" },
  "Coğrafya": { card: "bg-orange-50 border-orange-200", title: "text-orange-700", pill: "bg-orange-500" },
  "Felsefe": { card: "bg-purple-50 border-purple-200", title: "text-purple-700", pill: "bg-purple-600" },
  "Din Kültürü": { card: "bg-violet-50 border-violet-200", title: "text-violet-700", pill: "bg-violet-600" },
};
const st = (s) => SUBJ_STYLE[s] || { card: "bg-slate-50 border-slate-200", title: "text-slate-700", pill: "bg-slate-600" };

function mondayOf(offset) {
  const d = new Date();
  const dow = (d.getDay() + 6) % 7;
  d.setDate(d.getDate() - dow + offset * 7);
  return d;
}
const pad = (n) => String(n).padStart(2, "0");
const fmtShort = (d) => `${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const fmtFull = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

function AutoPlanButton({ studentId, reload }) {
  const [busy, setBusy] = useState(false);
  const run = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/analytics/auto-plan/${studentId}`);
      toast.success(`Zayıf konulara göre ${data.created} ders eklendi`);
      reload();
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Button onClick={run} disabled={busy} className="bg-indigo-600 hover:bg-indigo-700" data-testid="auto-plan-btn">
      {busy ? <Loader2 className="animate-spin" size={16} /> : <Wand2 size={16} />}
      <span className="ml-1">Otomatik Program</span>
    </Button>
  );
}

export default function WeeklySchedule({ studentId, readOnly, track }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [week, setWeek] = useState(1);
  const [openDay, setOpenDay] = useState(null);
  const [resForms, setResForms] = useState({});
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ week: 1, day: "", time: "", subject: "", resource_name: "", unit_no: "", unit_name: "", total: "" });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/weekly-schedule/${studentId}`);
      setItems(data);
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setLoading(false);
    }
  }, [studentId]);

  useEffect(() => {
    if (studentId) load();
  }, [load, studentId]);

  const weekItems = items.filter((i) => Number(i.week || 1) === week);
  const start = mondayOf(week - 1);
  const end = new Date(start);
  end.setDate(start.getDate() + 6);
  const hedef = weekItems.reduce((a, i) => a + (Number(i.total) || 0), 0);

  const alan = track?.includes("Sayısal") ? "Sayısal" : track?.includes("Sözel") ? "Sözel" : track?.includes("Eşit") ? "Eşit Ağırlık" : null;
  const alanCls = alan === "Sayısal" ? "bg-blue-600" : alan === "Sözel" ? "bg-amber-500" : "bg-purple-600";
  const subjects = subjectsForTrack(track);

  const addItem = async (e) => {
    e.preventDefault();
    if (!form.day || !form.subject) return toast.error("Gün ve ders zorunlu");
    setSaving(true);
    try {
      await api.post(`/weekly-schedule/${studentId}`, {
        week: Number(form.week), day: form.day, time: form.time, subject: form.subject,
        resource_name: form.resource_name, unit_no: form.unit_no, unit_name: form.unit_name,
        total: Number(form.total || 0), correct: 0, wrong: 0, blank: 0, status: "Bekliyor",
        exam: examOfTrack(track),
      });
      toast.success("Ders eklendi");
      setForm({ ...form, time: "", subject: "", resource_name: "", unit_no: "", unit_name: "", total: "" });
      load();
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setSaving(false);
    }
  };

  const saveResult = async (s) => {
    const f = resForms[s.id] || {};
    try {
      await api.put(`/weekly-schedule/${studentId}/${s.id}`, {
        ...s,
        total: Number(f.total ?? s.total ?? 0), correct: Number(f.correct ?? s.correct ?? 0),
        wrong: Number(f.wrong ?? s.wrong ?? 0), blank: Number(f.blank ?? s.blank ?? 0), status: "Tamamlandı",
      });
      toast.success("Sonuç kaydedildi");
      load();
    } catch (e) {
      toast.error(apiErr(e));
    }
  };

  const uncomplete = async (s) => {
    try {
      await api.put(`/weekly-schedule/${studentId}/${s.id}`, { ...s, status: "Bekliyor" });
      load();
    } catch (e) {
      toast.error(apiErr(e));
    }
  };

  const del = async (id) => {
    try {
      await api.delete(`/weekly-schedule/${studentId}/${id}`);
      setItems((p) => p.filter((x) => x.id !== id));
    } catch (e) {
      toast.error(apiErr(e));
    }
  };

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="text-xs font-semibold text-slate-400 flex items-center gap-1.5"><CalendarDays size={14} /> Haftalık Program</div>
          <h3 className="font-display text-3xl font-extrabold text-slate-900 tracking-tight">{week}. Hafta</h3>
          <div className="text-sm text-slate-500 mt-1">{fmtFull(start)} — {fmtFull(end)} · Haftalık hedef: <b className="text-slate-800">{hedef}</b> soru</div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {!readOnly && <AutoPlanButton studentId={studentId} reload={load} />}
          <Button variant="outline" className="border-slate-300" onClick={() => window.print()} data-testid="program-print-btn">
            <Printer size={15} /> <span className="ml-1">Yazdır / PDF</span>
          </Button>
          <div className="flex items-center gap-1">
            <button disabled={week <= 1} onClick={() => setWeek(week - 1)} className="w-8 h-8 rounded-lg border border-slate-300 flex items-center justify-center disabled:opacity-40 hover:bg-slate-50" data-testid="week-prev"><ChevronLeft size={16} /></button>
            <select className="ta-input w-28 text-center font-semibold" value={week} onChange={(e) => setWeek(Number(e.target.value))} data-testid="week-select">
              {Array.from({ length: 12 }, (_, i) => i + 1).map((w) => <option key={w} value={w}>{w}. Hafta</option>)}
            </select>
            <button disabled={week >= 12} onClick={() => setWeek(week + 1)} className="w-8 h-8 rounded-lg border border-slate-300 flex items-center justify-center disabled:opacity-40 hover:bg-slate-50" data-testid="week-next"><ChevronRight size={16} /></button>
          </div>
        </div>
      </div>

      {/* Add form */}
      {!readOnly && (
        <form onSubmit={addItem} className="card-ta p-4">
          <div className="text-sm font-semibold text-slate-700 mb-3">Programa Ders Ekle</div>
          <div className="grid grid-cols-2 sm:grid-cols-4 xl:grid-cols-7 gap-3">
            <div>
              <label className="ta-label">Hafta</label>
              <select className="ta-input" value={form.week} onChange={(e) => setForm({ ...form, week: e.target.value })} data-testid="ws-week">
                {Array.from({ length: 12 }, (_, i) => i + 1).map((w) => <option key={w} value={w}>{w}. Hafta</option>)}
              </select>
            </div>
            <div>
              <label className="ta-label">Gün</label>
              <select className="ta-input" value={form.day} onChange={(e) => setForm({ ...form, day: e.target.value })} data-testid="ws-day">
                <option value="">Seç</option>{DAYS.map((d) => <option key={d} value={d}>{d}</option>)}
              </select>
            </div>
            <div>
              <label className="ta-label">Ders</label>
              <select className="ta-input" value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} data-testid="ws-subject">
                <option value="">Seç</option>{subjects.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div>
              <label className="ta-label">Saat</label>
              <input className="ta-input" list="ws-etut-options" value={form.time} onChange={(e) => setForm({ ...form, time: e.target.value })} placeholder="14:00 veya 1. Etüt" data-testid="ws-time" />
              <datalist id="ws-etut-options">{["1. Etüt", "2. Etüt", "3. Etüt", "4. Etüt", "5. Etüt", "6. Etüt", "7. Etüt", "8. Etüt"].map((o) => <option key={o} value={o} />)}</datalist>
            </div>
            <div>
              <label className="ta-label">Kaynak</label>
              <input className="ta-input" value={form.resource_name} onChange={(e) => setForm({ ...form, resource_name: e.target.value })} placeholder="345 Matematik" data-testid="ws-resource" />
            </div>
            <div>
              <label className="ta-label">Ünite/Konu</label>
              <input className="ta-input" value={form.unit_name} onChange={(e) => setForm({ ...form, unit_name: e.target.value })} placeholder="Türev" data-testid="ws-unit" />
            </div>
            <div>
              <label className="ta-label">Hedef Soru</label>
              <input className="ta-input" type="number" value={form.total} onChange={(e) => setForm({ ...form, total: e.target.value })} placeholder="200" data-testid="ws-total" />
            </div>
          </div>
          <Button type="submit" disabled={saving} className="mt-3 bg-slate-800 hover:bg-slate-900" data-testid="ws-add-btn">
            {saving ? <Loader2 className="animate-spin" size={15} /> : <Plus size={15} />}<span className="ml-1">Programa Ekle</span>
          </Button>
        </form>
      )}

      {/* Day cards */}
      {loading ? (
        <div className="py-10 text-center text-slate-500"><Loader2 className="animate-spin inline" size={22} /></div>
      ) : (
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
          {DAYS.map((d, i) => {
            const dayItems = weekItems.filter((x) => x.day === d);
            const date = new Date(start);
            date.setDate(start.getDate() + i);
            const subjects = [...new Set(dayItems.map((x) => x.subject))].join(" + ");
            return (
              <div key={d} className="card-ta p-4" data-testid={`day-card-${d}`}>
                <div className="flex items-center justify-between mb-1">
                  <div className="flex items-center gap-2">
                    <span className="font-display font-bold text-lg text-slate-900">{d}</span>
                    {track && <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full text-white ${alanCls}`}>{track}</span>}
                  </div>
                  <span className="text-xs text-slate-400">{fmtShort(date)}</span>
                </div>
                <div className="text-xs text-slate-500 mb-3">{subjects || "Ders planlanmadı"}</div>
                <div className="space-y-2">
                  {dayItems.map((s) => {
                    const style = st(s.subject);
                    const done = s.status === "Tamamlandı";
                    return (
                      <div key={s.id} className={`rounded-lg border p-3 transition-colors ${done ? "bg-emerald-50 border-emerald-300" : style.card}`} data-testid={`ws-item-${s.id}`}>
                        <div className="flex items-center justify-between gap-2">
                          <div className={`text-sm font-bold ${done ? "text-emerald-700" : style.title}`}>{s.subject}</div>
                          <div className="flex items-center gap-2">
                            <span className={`text-[11px] font-bold text-white px-2 py-0.5 rounded-full ${done ? "bg-emerald-600" : style.pill}`}>{s.total || 0} soru</span>
                            {!readOnly && <button onClick={() => del(s.id)} className="text-slate-300 hover:text-rose-600"><Trash2 size={13} /></button>}
                          </div>
                        </div>
                        <div className="text-sm font-semibold text-slate-800 mt-0.5">{s.unit_name || "-"}</div>
                        <div className="text-[11px] text-slate-500 flex items-center gap-1 mt-0.5">
                          <BookOpen size={11} /> {s.resource_name || "Kaynak yok"}{s.unit_no ? ` · Ünite ${s.unit_no}` : ""}{s.time ? ` · ${s.time}` : ""}
                        </div>
                        {done && (
                          <div className="mt-1.5 flex flex-wrap gap-x-3 gap-y-1 text-[11px]">
                            <span className="text-slate-500">Top: <b>{s.total}</b></span>
                            <span className="text-emerald-700">D: <b>{s.correct || 0}</b></span>
                            <span className="text-rose-700">Y: <b>{s.wrong || 0}</b></span>
                            <span className="text-slate-500">Boş: <b>{s.blank || 0}</b></span>
                            <span className="text-indigo-700">Net: <b>{Number(s.net || 0).toFixed(2)}</b></span>
                          </div>
                        )}
                      </div>
                    );
                  })}
                  {dayItems.length === 0 && <div className="text-xs text-slate-300 text-center py-4">Bu güne ders eklenmedi.</div>}
                </div>
                {!readOnly && dayItems.length > 0 && (
                  <div className="mt-3 space-y-2">
                    <Button variant="outline" className="w-full h-8 text-xs border-slate-300" onClick={() => setOpenDay(openDay === d ? null : d)} data-testid={`result-btn-${d}`}>
                      <Plus size={13} /> <span className="ml-1">Ödev Sonucu Gir (Soru / D / Y / B)</span>
                    </Button>
                    {openDay === d &&
                      dayItems.map((s) => (
                        <div key={s.id} className="rounded-lg border border-slate-200 bg-white p-2.5 space-y-2">
                          <div className="text-xs font-semibold text-slate-700">{s.subject} · {s.unit_name || "-"}</div>
                          <div className="grid grid-cols-4 gap-1.5">
                            {["total", "correct", "wrong", "blank"].map((f) => (
                              <input key={f} type="number" className="ta-input px-1.5 py-1 text-xs" placeholder={{ total: "Top", correct: "D", wrong: "Y", blank: "Boş" }[f]}
                                value={resForms[s.id]?.[f] ?? s[f] ?? ""}
                                onChange={(e) => setResForms({ ...resForms, [s.id]: { ...(resForms[s.id] || {}), [f]: e.target.value } })}
                                data-testid={`res-${f}-${s.id}`} />
                            ))}
                          </div>
                          <div className="flex gap-1.5">
                            <Button size="sm" className="h-7 text-xs bg-emerald-600 hover:bg-emerald-700 flex-1" onClick={() => saveResult(s)} data-testid={`res-save-${s.id}`}>Kaydet & Tamamla</Button>
                            {s.status === "Tamamlandı" && (
                              <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => uncomplete(s)}><RotateCcw size={12} /></Button>
                            )}
                          </div>
                        </div>
                      ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Full-week table */}
      <div className="card-ta overflow-hidden">
        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200 flex-wrap gap-2">
          <div className="flex items-center gap-2 font-display font-bold text-slate-900"><Table size={16} className="text-indigo-600" /> Günlük Çalışma Sistemi — Tüm Hafta</div>
          <div className="text-xs text-slate-400">{fmtFull(start)} — {fmtFull(end)} · Hedef {hedef} soru</div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-200 text-left text-slate-500 bg-slate-50">
                <th className="px-4 py-2.5 font-medium">Gün</th>
                <th className="px-4 py-2.5 font-medium">Ders / Konu</th>
                <th className="px-4 py-2.5 font-medium">Saat</th>
                <th className="px-4 py-2.5 font-medium">Hedef</th>
                <th className="px-4 py-2.5 font-medium">Durum</th>
              </tr>
            </thead>
            <tbody data-testid="week-table">
              {weekItems.length === 0 ? (
                <tr><td colSpan={5} className="px-4 py-8 text-center text-slate-400">Bu hafta için program yok.</td></tr>
              ) : (
                DAYS.flatMap((d, di) => {
                  const date = new Date(start);
                  date.setDate(start.getDate() + di);
                  return weekItems.filter((x) => x.day === d).map((s, idx) => (
                    <tr key={s.id} className="border-b border-slate-100">
                      {idx === 0 && (
                        <td className="px-4 py-3 align-top" rowSpan={weekItems.filter((x) => x.day === d).length}>
                          <div className="font-bold text-slate-900">{d}</div>
                          <div className="text-[11px] text-slate-400">{fmtShort(date)}</div>
                        </td>
                      )}
                      <td className="px-4 py-3">
                        <div className={`font-semibold ${st(s.subject).title}`}>{s.subject}</div>
                        <div className="text-slate-800">{s.unit_name || "-"}</div>
                        <div className="text-[11px] text-slate-400">{s.resource_name || ""}</div>
                      </td>
                      <td className="px-4 py-3 text-slate-500">{s.time || "—"}</td>
                      <td className="px-4 py-3 font-semibold text-slate-700">{s.total || 0} soru</td>
                      <td className="px-4 py-3">
                        <span className={`text-xs px-2 py-0.5 rounded-full border ${s.status === "Tamamlandı" ? "bg-emerald-100 text-emerald-700 border-emerald-200" : "bg-amber-100 text-amber-700 border-amber-200"}`}>
                          {s.status || "Bekliyor"}
                        </span>
                      </td>
                    </tr>
                  ));
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

